import { useCallback, useContext, useEffect } from 'react';
import { UserInfoContext } from '@/contexts/UserInfoContext';

/**
 * Listens for messages from a window that we spawned with `window.open()`, which may
 * request the user authentication info from us. This is typically used when opening
 * a document artifact as a Printable Version in a new stripped down window.
 */
export const useChildWindowRequestHandler = () => {
  const { userMetadata } = useContext(UserInfoContext);
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (event.data?.type === 'USER_CREDENTIALS_REQUESTED') {
        event.source?.postMessage(
          {
            type: 'USER_CREDENTIALS',
            data: JSON.parse(userMetadata || '{}'),
          },
          { targetOrigin: window.location.origin }
        );
        console.debug('Devo Studio: sent user info to child window.');
      }
    },
    [userMetadata]
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);
};

// For convenient composition, a component that wraps the useChildWindowRequest hook.
export const ChildWindowRequestHandler = () => {
  useChildWindowRequestHandler();
  return null;
};
